import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Music, Film, Send, ChevronLeft, Globe, BarChart3, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import PageTransition, { Section } from "@/components/site/PageTransition";
import Seo from "@/components/site/Seo";
import Modal from "@/components/site/Modal";

const MUSIC_INIT = { name: "", stage_name: "", release_frequency: "", current_distributor: "", instagram: "", spotify: "", youtube: "", apple_music: "", website: "", email: "" };
const FILM_INIT  = { name: "", company_name: "", location: "", email: "", imdb: "", instagram: "", youtube: "", reel_links: "" };

export default function Distribution() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState(null); // 'music' | 'film' | null
  const [music, setMusic] = useState(MUSIC_INIT);
  const [film, setFilm] = useState(FILM_INIT);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => { setType(null); setMusic(MUSIC_INIT); setFilm(FILM_INIT); };
  const close = () => { setOpen(false); setTimeout(reset, 300); };

  const submitMusic = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/inquiries/distribution/music", music);
      toast.success("Music submission received. We review every entry.");
      close();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Submission failed"); }
    finally { setSubmitting(false); }
  };
  const submitFilm = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/inquiries/distribution/film", film);
      toast.success("Film submission received. We review every entry.");
      close();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Submission failed"); }
    finally { setSubmitting(false); }
  };

  return (
    <PageTransition>
      <Seo title="Distribution" description="Global music distribution to 230+ countries via Empire & Virgin Music. Sign with Pulse Media." />
      <section className="pt-40 pb-12 px-6 lg:px-10" data-testid="distribution-hero">
        <div className="max-w-7xl mx-auto grid md:grid-cols-[1fr_auto] gap-12 items-center">
          <div className="max-w-3xl">
            <div className="label-mono mb-4">/ Distribution</div>
            <h1 className="font-display text-5xl md:text-7xl font-bold tracking-tighter leading-[1.0]">
              230+ countries.<br/><span className="gradient-text">80+ service providers.</span>
            </h1>
            <p className="mt-8 text-lg text-zinc-300 leading-relaxed">
              Pulse Media pushes your music to over 80+ DSPs in 230+ countries. We collect your royalty money monthly.
              Our distribution services are provided through Empire Distribution + Virgin Music Group, a division of Universal —
              to deliver best-in-class services to artists and bands worldwide.
            </p>
          </div>
          <div className="hidden md:flex items-center justify-center">
            <img
              src="/assets/partners-empire-virgin.png"
              alt="Distribution partners — Empire Distribution and Virgin Music Group"
              className="w-[260px] h-auto select-none pointer-events-none"
              data-testid="distro-partner-logos"
              style={{ filter: "drop-shadow(0 0 30px rgba(0,184,255,0.18))" }}
            />
          </div>
        </div>
      </section>

      <Section className="px-6 lg:px-10 py-16 border-t border-white/5">
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-6">
          {[
            { Icon: Globe, label: "Global Reach", value: "230+ Countries", desc: "Spotify, Apple Music, YouTube Music, Amazon, Tidal, Deezer, and 75+ more." },
            { Icon: DollarSign, label: "Royalty Collection", value: "Monthly", desc: "Transparent statements. Direct deposit. No surprise deductions." },
            { Icon: BarChart3, label: "Analytics + Backend", value: "Best-in-class", desc: "Powered by Empire Distribution + Virgin / Universal infrastructure." },
          ].map((b, i) => (
            <motion.div
              key={b.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.07 }}
              className="p-8 rounded-2xl border border-white/5 bg-[#121215] lift"
            >
              <b.Icon size={28} strokeWidth={1.5} className="text-cyan-400" />
              <div className="label-mono mt-5 mb-1">/ {b.label}</div>
              <div className="font-display text-2xl font-semibold tracking-tight mb-3">{b.value}</div>
              <p className="text-zinc-400 text-sm leading-relaxed">{b.desc}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      <Section className="px-6 lg:px-10 py-24 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter">Submit for distribution.</h2>
          <p className="mt-4 text-zinc-400">Music or film — we evaluate every submission within two weeks.</p>
          <button onClick={() => setOpen(true)} className="glass-btn solid mt-8" data-testid="distro-submit-open">
            Submit Now <Send size={14} />
          </button>
        </div>
      </Section>

      <Modal open={open} onClose={close} wide title={type ? (type === "music" ? "Music Distribution Inquiry" : "Film Distribution Inquiry") : "Submit for distribution"} testid="distro-modal">
        <AnimatePresence mode="wait">
          {!type && (
            <motion.div key="picker" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="grid md:grid-cols-2 gap-5">
              <button
                onClick={() => setType("music")}
                className="group relative rounded-2xl overflow-hidden border border-white/10 lift p-10 text-left"
                style={{ background: "linear-gradient(135deg, rgba(0,184,255,0.08) 0%, rgba(0,184,255,0) 60%)" }}
                data-testid="distro-pick-music"
              >
                <Music size={32} strokeWidth={1.5} className="text-cyan-400 mb-6 group-hover:scale-110 transition-transform" />
                <div className="font-display text-3xl font-bold tracking-tight">Music</div>
                <p className="mt-2 text-zinc-400 text-sm">Singles, EPs, albums — for solo artists, bands, and producers.</p>
              </button>
              <button
                onClick={() => setType("film")}
                className="group relative rounded-2xl overflow-hidden border border-white/10 lift p-10 text-left"
                style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(0,184,255,0.04) 60%)" }}
                data-testid="distro-pick-film"
              >
                <Film size={32} strokeWidth={1.5} className="text-cyan-400 mb-6 group-hover:scale-110 transition-transform" />
                <div className="font-display text-3xl font-bold tracking-tight">Film</div>
                <p className="mt-2 text-zinc-400 text-sm">Features, docs, shorts — for filmmakers and production companies.</p>
              </button>
            </motion.div>
          )}

          {type === "music" && (
            <motion.form key="music" onSubmit={submitMusic} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} className="space-y-4" data-testid="distro-music-form">
              <button type="button" onClick={() => setType(null)} className="text-zinc-400 hover:text-white text-xs flex items-center gap-1 mb-2"><ChevronLeft size={14}/> Back</button>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Name *" required value={music.name} onChange={(v) => setMusic({...music, name: v})} testid="dm-name" />
                <Field label="Stage Name *" required value={music.stage_name} onChange={(v) => setMusic({...music, stage_name: v})} testid="dm-stage" />
                <Field label="Release Frequency *" placeholder="e.g. monthly, quarterly" required value={music.release_frequency} onChange={(v) => setMusic({...music, release_frequency: v})} testid="dm-freq" />
                <Field label="Current Distributor" placeholder="DistroKid, TuneCore, none..." value={music.current_distributor} onChange={(v) => setMusic({...music, current_distributor: v})} testid="dm-distro" />
                <Field label="Email *" type="email" required value={music.email} onChange={(v) => setMusic({...music, email: v})} testid="dm-email" />
                <Field label="Instagram" value={music.instagram} onChange={(v) => setMusic({...music, instagram: v})} testid="dm-ig" />
                <Field label="Spotify" value={music.spotify} onChange={(v) => setMusic({...music, spotify: v})} testid="dm-sp" />
                <Field label="YouTube" value={music.youtube} onChange={(v) => setMusic({...music, youtube: v})} testid="dm-yt" />
                <Field label="Apple Music" value={music.apple_music} onChange={(v) => setMusic({...music, apple_music: v})} testid="dm-am" />
                <Field label="Website (optional)" value={music.website} onChange={(v) => setMusic({...music, website: v})} testid="dm-web" />
              </div>
              <button type="submit" disabled={submitting} className="glass-btn solid w-full justify-center mt-4" data-testid="dm-submit">
                {submitting ? "Submitting..." : "Submit Music Inquiry"} <Send size={14} />
              </button>
            </motion.form>
          )}

          {type === "film" && (
            <motion.form key="film" onSubmit={submitFilm} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} className="space-y-4" data-testid="distro-film-form">
              <button type="button" onClick={() => setType(null)} className="text-zinc-400 hover:text-white text-xs flex items-center gap-1 mb-2"><ChevronLeft size={14}/> Back</button>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Name *" required value={film.name} onChange={(v) => setFilm({...film, name: v})} testid="df-name" />
                <Field label="Company Name *" required value={film.company_name} onChange={(v) => setFilm({...film, company_name: v})} testid="df-company" />
                <Field label="Location *" required value={film.location} onChange={(v) => setFilm({...film, location: v})} testid="df-location" />
                <Field label="Email *" type="email" required value={film.email} onChange={(v) => setFilm({...film, email: v})} testid="df-email" />
                <Field label="IMDb" value={film.imdb} onChange={(v) => setFilm({...film, imdb: v})} testid="df-imdb" />
                <Field label="Instagram" value={film.instagram} onChange={(v) => setFilm({...film, instagram: v})} testid="df-ig" />
                <Field label="YouTube" value={film.youtube} onChange={(v) => setFilm({...film, youtube: v})} testid="df-yt" />
              </div>
              <div>
                <label className="label-mono block mb-2">Reel / Project Links</label>
                <textarea className="pulse-input pulse-textarea" placeholder="Paste links separated by commas or new lines" value={film.reel_links} onChange={(e) => setFilm({...film, reel_links: e.target.value})} data-testid="df-reels" />
              </div>
              <button type="submit" disabled={submitting} className="glass-btn solid w-full justify-center mt-4" data-testid="df-submit">
                {submitting ? "Submitting..." : "Submit Film Inquiry"} <Send size={14} />
              </button>
            </motion.form>
          )}
        </AnimatePresence>
      </Modal>
    </PageTransition>
  );
}

function Field({ label, value, onChange, type = "text", required = false, placeholder = "", testid }) {
  return (
    <div>
      <label className="label-mono block mb-2">{label}</label>
      <input className="pulse-input" type={type} required={required} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}
